import { t as tr } from "../../i18n";
import { rcCounterfortPositions, rcCounterfortProfile, rcFrame, type Pt, type WallInput } from "@tasduvar/engine";
import { fitTransform, pathOf, toSvg, type Transform } from "./geometry";
import { SvgDefs } from "./SectionDrawing";

/**
 * Payandalı duvar plan görünümü (üstten): yatay eksen duvar boyu (z, 0…L), düşey eksen taban genişliği (x, burun altta, topuk üstte).
 * Taban plağı, gövde şeridi, payandalar (kesit profilinin tabandaki izi: gövde arkası → topuk ucu) ve kayma dişi (gizli, kesikli).
 * Ölçüler: L, ilk payanda s/2, aralık s, kalınlık, B. Şematiktir; yalnız payandalı betonarme duvarda çizilir.
 */
export function PlanView({ wall, width = 720, height = 320, className }: { wall: WallInput; width?: number; height?: number; className?: string }): JSX.Element | null {
  const g = wall.geometry;
  if (g.mode !== "rc-cantilever" || !g.counterforts) return null;
  const f = rcFrame(g), cf = g.counterforts, L = wall.length ?? 10;
  const zs = rcCounterfortPositions(g, L);
  const prof = rcCounterfortProfile(g)!;
  const xCfEnd = Math.max(...prof.map((p) => p.x));
  // Plan koordinatı: (u = z, v = x) → geometry.ts dönüşümü (v yukarı) ile aynı yardımcılar
  const pad = Math.max(0.6, 0.12 * L);
  const t: Transform = fitTransform({ minX: -0.8 * pad, minY: -1.1 * Math.max(0.6, 0.1 * f.B) - 0.6, maxX: L + 0.4 * pad, maxY: f.B + 0.7 }, width, height, 20);
  const P = (z: number, x: number): Pt => ({ x: z, y: x });
  const rect = (z0: number, z1: number, x0: number, x1: number): Pt[] => [P(z0, x0), P(z1, x0), P(z1, x1), P(z0, x1)];
  const dim = (a: Pt, b: Pt, label: string, key: string): JSX.Element => {
    const A = toSvg(t, a), Bp = toSvg(t, b), vertical = Math.abs(a.x - b.x) < 1e-9;
    return (
      <g key={key} data-plan-dim={key}>
        <line x1={A.x} y1={A.y} x2={Bp.x} y2={Bp.y} markerStart="url(#arrow)" markerEnd="url(#arrow)" />
        <text x={vertical ? A.x - 4 : (A.x + Bp.x) / 2} y={vertical ? (A.y + Bp.y) / 2 : A.y - 4} textAnchor={vertical ? "end" : "middle"} dominantBaseline={vertical ? "middle" : "auto"} stroke="none" className="num">{label}</text>
      </g>
    );
  };
  const fm = (v: number): string => v.toFixed(2).replace(".", ",");
  const yDim = -Math.max(0.6, 0.1 * f.B);
  return (
    <svg role="img" aria-label={tr("Payanda plan görünümü")} viewBox={`0 0 ${width} ${height}`} width={width} height={height} className={className}>
      <SvgDefs />
      <path data-part="plan-base" d={pathOf(t, rect(0, L, 0, f.B))} fill="url(#concrete)" stroke="#1F2937" strokeWidth={1.4} />
      <path data-part="plan-stem" d={pathOf(t, rect(0, L, f.xStemFront, f.xStemBack))} fill="#9CA3AF" fillOpacity={0.35} stroke="#1F2937" strokeWidth={1} />
      {g.key ? <path data-part="plan-key" d={pathOf(t, rect(0, L, g.key.offsetFromToe, g.key.offsetFromToe + g.key.width))} fill="none" stroke="#4B5563" strokeDasharray="4 3" /> : null}
      {zs.map((z, i) => <path key={i} data-part="plan-counterfort" d={pathOf(t, rect(z - cf.thickness / 2, z + cf.thickness / 2, f.xStemBack, xCfEnd))} fill="#6B7280" fillOpacity={0.45} stroke="#1F2937" strokeWidth={1} />)}
      <g className="text-[10px] fill-stone" stroke="#6B7280" strokeWidth={0.8}>
        {dim(P(0, yDim), P(L, yDim), `L = ${fm(L)} m`, "L")}
        {zs.length ? dim(P(0, f.B + 0.35), P(zs[0]!, f.B + 0.35), `${fm(zs[0]!)}`, "s0") : null}
        {zs.length > 1 ? dim(P(zs[0]!, f.B + 0.35), P(zs[1]!, f.B + 0.35), `s = ${fm(cf.spacing)} m`, "s") : null}
        {dim(P(-0.5 * pad, 0), P(-0.5 * pad, f.B), `B = ${fm(f.B)} m`, "B")}
        {zs.length ? (() => { const s = toSvg(t, P(zs[0]!, f.xStemBack + 0.45 * (xCfEnd - f.xStemBack))); return <text x={s.x + 4 + (cf.thickness / 2) * t.scale} y={s.y} dominantBaseline="middle" className="num" paintOrder="stroke" stroke="#fff" strokeWidth={3}>t = {fm(cf.thickness)} m</text>; })() : null}
      </g>
      {([[(f.xStemFront + f.xStemBack) / 2, tr("gövde")], [0, tr("burun")], [f.B, tr("topuk")]] as const).map(([x, label]) => (
        <text key={label} x={toSvg(t, P(L, 0)).x + 4} y={toSvg(t, P(L, x)).y} dominantBaseline="middle" className="text-[10px] fill-stone">{label}</text>
      ))}
    </svg>
  );
}
